import React, { forwardRef, useId } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../theme';
import type { InputProps } from './Input.types';

/**
 * Input variants using class-variance-authority
 */
export const inputVariants = cva(
  // Base styles
  [
    'w-full bg-cream border-2 border-transparent rounded-md',
    'font-body text-navy placeholder:text-warm-gray',
    'transition-all duration-200',
    'focus:outline-none focus:border-sage focus:ring-4 focus:ring-sage/15',
    'hover:border-sage-light',
    'disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:border-transparent',
  ],
  {
    variants: {
      size: {
        sm: 'h-10 px-3 text-sm',
        md: 'h-12 px-4 text-base',
        lg: 'h-14 px-5 text-lg',
      },
      hasError: {
        true: 'border-red-400 focus:border-red-500 focus:ring-red-500/15 hover:border-red-400',
        false: '',
      },
      hasLeftIcon: {
        true: 'pl-10',
        false: '',
      },
      hasRightIcon: {
        true: 'pr-10',
        false: '',
      },
    },
    compoundVariants: [
      { hasLeftIcon: true, size: 'sm', className: 'pl-9' },
      { hasLeftIcon: true, size: 'lg', className: 'pl-12' },
      { hasRightIcon: true, size: 'sm', className: 'pr-9' },
      { hasRightIcon: true, size: 'lg', className: 'pr-12' },
    ],
    defaultVariants: {
      size: 'md',
      hasError: false,
      hasLeftIcon: false,
      hasRightIcon: false,
    },
  }
);

export type InputVariantProps = VariantProps<typeof inputVariants>;

/**
 * Input component following the BrightPath Design System.
 * 
 * A text input with support for labels, helper text, error states,
 * and leading/trailing icons.
 * 
 * @example
 * // Basic input
 * <Input placeholder="Enter your email" />
 * 
 * // With label and helper text
 * <Input 
 *   label="Email" 
 *   helperText="We'll never share your email."
 *   placeholder="you@example.com"
 * />
 * 
 * // With error
 * <Input 
 *   label="Email" 
 *   error="Please enter a valid email address"
 * />
 * 
 * // With icons
 * <Input 
 *   leftIcon={<Mail />}
 *   rightIcon={<Check />}
 * />
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      wrapperClassName,
      label,
      helperText,
      error,
      size = 'md',
      leftIcon,
      rightIcon,
      fullWidth = true,
      id: providedId,
      disabled,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const id = providedId ?? generatedId;
    const hasError = !!error;

    // Icon sizes based on input size
    const iconSizeClass = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-5 w-5',
    }[size];

    const iconPositionClass = {
      sm: 'top-3',
      md: 'top-3.5',
      lg: 'top-4',
    }[size];

    return (
      <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full', wrapperClassName)}>
        {label && (
          <label
            htmlFor={id}
            className={cn(
              'font-medium text-sm text-navy',
              disabled && 'opacity-60'
            )}
          >
            {label}
          </label>
        )}
        
        <div className="relative">
          {leftIcon && (
            <span
              className={cn(
                'absolute left-3 text-warm-gray pointer-events-none',
                iconPositionClass,
                iconSizeClass
              )}
            >
              {leftIcon}
            </span>
          )}
          
          <input
            ref={ref}
            id={id}
            disabled={disabled}
            className={cn(
              inputVariants({
                size,
                hasError,
                hasLeftIcon: !!leftIcon,
                hasRightIcon: !!rightIcon,
              }),
              className
            )}
            aria-invalid={hasError}
            aria-describedby={
              error ? `${id}-error` : helperText ? `${id}-helper` : undefined
            }
            {...props}
          />
          
          {rightIcon && (
            <span
              className={cn(
                'absolute right-3 text-warm-gray',
                iconPositionClass,
                iconSizeClass
              )}
            >
              {rightIcon}
            </span>
          )}
        </div>
        
        {error && (
          <p
            id={`${id}-error`}
            className="text-sm text-red-500 flex items-center gap-1"
            role="alert"
          >
            {error}
          </p>
        )}
        
        {helperText && !error && (
          <p
            id={`${id}-helper`}
            className="text-sm text-warm-gray"
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
